<script>
  import { createPopper } from "@popperjs/core";
  import { onMount, onDestroy } from "svelte"; 
  import pb from '../../lib/pocketbase'; 
  import { toasts } from "svelte-toasts";

  let dropdownPopoverShow = false;
  let btnDropdownRef;
  let popoverDropdownRef;

  let notifications = [];
  let unreadCount = 0;
  let subscription = null; // Add subscription reference

  // Array para llevar registro de notificaciones que ya se han mostrado en toast
  let shownNotifications = new Set();

  const toggleDropdown = async (event) => {
    event.preventDefault();
    dropdownPopoverShow = !dropdownPopoverShow;
    if (dropdownPopoverShow) {
      createPopper(btnDropdownRef, popoverDropdownRef, {
        placement: "bottom-start",
      });
      
      // Mark all notifications as read when opening the dropdown
      try {
        for (const notification of notifications) {
          if (notification.sta === true) {
            await pb.collection("notificacion").update(notification.id, { sta: false });
          }
        }
        // Update local state
        notifications = notifications.map(notification => ({ ...notification, sta: false }));
        unreadCount = 0;
      } catch (error) {
        console.error("Error marking notifications as read:", error);
      }
    }
  };

  const fetchNotifications = async () => {
    try {
      console.log('Fetching notifications...'); // Debug log
      const result = await pb.collection("notificacion").getFullList({ 
        filter: 'sta=true',
        sort: '-created',
        $autoCancel: false
      });
      console.log('Fetched notifications:', result); // Debug log
      notifications = result;
      unreadCount = notifications.length;
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  async function subscribeToPocketBase() {
    // Unsubscribe if there's an existing subscription
    if (subscription) {
      await pb.collection('notificacion').unsubscribe(subscription);
    }

    console.log('Subscribing to notifications...'); // Debug log
    subscription = await pb.collection('notificacion').subscribe('*', (e) => {
      console.log('Notification event received:', e); // Debug log

      if (e.action === 'create') {
        const newNotification = e.record;
        
        // Check if notification is too old (more than 5 minutes)
        const notificationTime = new Date(newNotification.created);
        const currentTime = new Date();
        const timeDiff = (currentTime - notificationTime) / 1000 / 60; // difference in minutes
        
        // Skip if notification is too old or already shown
        if (timeDiff > 5 || shownNotifications.has(newNotification.id)) {
          console.log('Skipping old or already shown notification:', newNotification.id);
          return;
        }

        // Check if notification already exists in the array using multiple criteria
        const notificationExists = notifications.some(n => 
          n.id === newNotification.id || 
          (n.mensaje === newNotification.mensaje && 
           Math.abs(new Date(n.created) - notificationTime) < 60000) // Within 1 minute
        );
        
        if (!notificationExists) {
          console.log('New notification:', newNotification); // Debug log
          notifications = [newNotification, ...notifications];
          unreadCount++;

          if (newNotification.sta === true) {
            toasts.add({
              description: newNotification.mensaje,
              duration: 3000,
              placement: 'bottom-right',
              type: 'success',
              theme: 'dark',
              showProgress: true
            });
            shownNotifications.add(newNotification.id);
            
            // Clean up old shown notifications (older than 1 hour)
            const oneHourAgo = new Date(currentTime - 60 * 60 * 1000);
            shownNotifications.forEach(id => {
              const notification = notifications.find(n => n.id === id);
              if (notification && new Date(notification.created) < oneHourAgo) {
                shownNotifications.delete(id);
              }
            });
          }
        }
      }
      unreadCount = notifications.filter(notification => notification.sta === true).length;
      console.log('Current notifications:', notifications); // Debug log
      console.log('Unread count:', unreadCount); // Debug log
    });
  }

  const acknowledgeNotification = async (notificationId) => {
    try {
      await pb.collection("notificacion").update(notificationId, { sta: false });
      notifications = notifications.filter(notification => notification.id !== notificationId);
      unreadCount = notifications.filter(notification => notification.sta === true).length;
    } catch (error) {
      console.error("Error updating notification status:", error);
    }
  };

  onMount(() => {
    console.log("Component mounted"); // Log para verificar el montaje
    fetchNotifications(); 
    subscribeToPocketBase();
  });

  onDestroy(() => {
    if (subscription) {
      pb.collection('notificacion').unsubscribe(subscription);
    }
  });

  // Function to format the date
  function formatDate(dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }
</script>

<div class="relative">
  <a
    class="text-white block py-1 px-3"
    href="#erpsys"
    bind:this="{btnDropdownRef}"
    on:click="{toggleDropdown}"
  >
    <i class="fas fa-bell notification-bell"></i>
    {#if unreadCount > 0} <!-- Show badge if there are unread notifications -->
      <span class="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center" style="padding: 2px;">
        {unreadCount}
      </span>
    {/if}
  </a>
  <div
    bind:this="{popoverDropdownRef}"
    class="bg-white text-base z-50 float-left py-2 list-none text-left rounded shadow-lg min-w-48 {dropdownPopoverShow ? 'block':'hidden'}"
  >

  {#if notifications.length > 0}
    {#each notifications as notification (notification.id)}
      {#if notification.sta === true}
        <a
          href="#erpsys" 
          on:click={() => acknowledgeNotification(notification.id)}
          class="text-sm py-2 px-4 font-normal block w-full whitespace-nowrap bg-transparent text-blueGray-700 flex items-center"
        >
          <span class="flex-1">{notification.mensaje}</span>
          <span class="ml-2 text-xs bg-blueGray-200 text-blueGray-700 rounded-full px-2 py-1">
            {formatDate(notification.created)}
          </span>
        </a>
      {/if}
    {/each}
  {:else}
    <span class="text-sm py-2 px-4 font-normal block w-full whitespace-nowrap text-blueGray-500">
      Estás al día con tus notificaciones
    </span>
  {/if}

  </div>
</div>
